import { useLocale } from '~/contexts/LocaleContext'
import { Image } from '~/components/ui/image'
import ArrowDown from './arrow-down'
import { LanguageCode } from '~/data/language'

const LANGUAGE_DETAILS = {
  [LanguageCode.VI]: { label: 'Tiếng Việt', flag: '/static/images/language-vn.png' },
  [LanguageCode.EN]: { label: 'English', flag: '/static/images/language-en.png' },
  [LanguageCode.ZH]: { label: '中文', flag: '/static/images/language-zh.png' },
  [LanguageCode.KO]: { label: '한국어', flag: '/static/images/language-ko.png' },
  [LanguageCode.RU]: { label: 'Русский', flag: '/static/images/language-ru.png' },
} as const

interface LanguageSelectorProps {
  isActive: boolean
  isLangMenuOpen: boolean
  setIsLangMenuOpen: (isOpen: boolean) => void
  langMenuRef: React.RefObject<HTMLDivElement>
}

export default function LanguageSelector({
  isActive,
  isLangMenuOpen,
  setIsLangMenuOpen,
  langMenuRef,
}: LanguageSelectorProps) {
  const { locale, setLocale } = useLocale()
  const handleLanguageChange = (langCode: LanguageCode) => {
    setLocale(langCode)
    setIsLangMenuOpen(false)
  }

  const currentLanguage = LANGUAGE_DETAILS[locale]

  return (
    <div className="relative" ref={langMenuRef}>
      <button
        onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
        aria-label="Language selector"
        className={`font-body flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-light leading-6 transition-all duration-300 ${
          isActive
            ? 'text-gray-900 hover:bg-gray-50 hover:text-primary-600'
            : 'text-white hover:bg-white/10 hover:text-white'
        }`}
      >
        <Image
          src={currentLanguage.flag}
          alt="Language"
          width={20}
          height={20}
          className="box-content rounded-full border border-gray-200"
        />
        <span className="font-medium">{locale.toString().toUpperCase()}</span>
        <div
          className={`transform transition-transform duration-200 ${isLangMenuOpen ? 'rotate-180' : ''}`}
        >
          <ArrowDown variant={isActive ? 'black' : 'white'} />
        </div>
      </button>

      {isLangMenuOpen && (
        <div className="absolute right-0 z-50 mt-2 w-36 rounded-lg border border-gray-100 bg-white py-1 shadow-lg">
          {Object.entries(LANGUAGE_DETAILS).map(([code, details]) => (
            <button
              key={code}
              onClick={() => {
                handleLanguageChange(code as LanguageCode)
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-50"
            >
              <Image
                src={details.flag}
                alt={details.label}
                width={16}
                height={16}
                className="box-content rounded-full border border-gray-200"
              />
              <div className="flex flex-col">
                <span className="text-xs font-light text-gray-500">{details.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
